import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { StorageHlp } from 'src/lib/fliko/helpers/storage.helper';
import { FileInStorageData } from 'src/app/shared/models/file.data';
import { DatabaseService } from 'src/app/shared/services/database.service';
import { PushIdService } from 'src/app/shared/services/pushId.service';
import { FireStorageService } from 'src/app/shared/services/storage.service';
import { AttFile, Attachments, FileCategory } from './models/attachments-model';

@Injectable({ providedIn: 'root' })
export class UsersDocumentService {
  constructor(
    private dbSrv: DatabaseService,
    private pushId: PushIdService,
    private fs: FireStorageService
  ) {}

  /**
   * @param {string} contextId unitId (community) || tenancyAgreementId (prs)
   * @param {string} resId     residentId
   * @path /user-documents/$contextId/$residentId/
   */
  private userDocuments = (contextId: string, resId: string) =>
    this.dbSrv.estateFactory.getObject<Attachments>(`user-documents/$eId/${contextId}/${resId}`);

  public async getFiles(contextId: string, landlordId: string, cancel$?: Observable<unknown>): Promise<AttFile[]> {
    const documents = await this.userDocuments(contextId, landlordId).snapshot(cancel$);
    // A missing record is null; undefined also covers cancellation, context changes and handled database errors.
    if (documents === undefined) throw new Error('User documents could not be read or the read was cancelled');
    const files: AttFile[] = documents?.files ?? [];
    files.forEach((f: AttFile) => (f.visibleInApp ??= true)); // in db can be undefined
    return files;
  }

  public async setFiles(contextId: string, landlordId: string, data: Attachments): Promise<void> {
    return this.dbSrv.estateFactory.updatePaths(data.estateId, {
      [`user-documents/${data.estateId}/${contextId}/${landlordId}`]: data
    });
  }

  public async uploadAttachment(contextId: string, landlordId: string, file: File): Promise<string> {
    const name = `${this.pushId.create()}-${file.name}`;
    const result = await this.fs.uploadFile(file, `static/user-attachments/${contextId}/${landlordId}/${name}`);
    return result.ref.fullPath;
  }

  /**
   * Update user docs of agreement landlords
   * @deprecated replaced by setAgreement() and setProtocol()
   * @param {string[]}     preExistingAttUrls  attachments to add for new landlords
   * @param {string[]}     addedAttachments    attachments to add for all landlords
   * @param {string[]}     deletedAttUrls      attachments to delete from all landlords
   * @param {string[]}     landlordsIds        old and new landlords
   * @param {string[]}     removedLandlordsIds landlords whose attachments should be deleted
   * @param {string}       estateId
   * @param {string}       agreementId
   * @param {string}       referenceId         agreement or protocol id
   * @param {FileCategory} category            category of attachments
   * @return {Promise<void[]>} List of all promises
   */
  public async updateUsersDocs(
    preExistingAttUrls: string[],
    addedAttachments: string[],
    deletedAttUrls: string[],
    landlordsIds: string[],
    removedLandlordsIds: string[],
    estateId: string,
    agreementId: string,
    referenceId: string,
    category: FileCategory
  ): Promise<void[]> {
    // TODO: clean this shit - protocols and agreements
    const urlToAttFile = (url: string) => {
      return {
        uploadDate: Date.now(),
        name: StorageHlp.pathToName(url),
        url: url,
        fileCategory: category,
        visibleInApp: true,
        referenceId: referenceId
      };
    };

    const oldAttFiles = preExistingAttUrls.map(urlToAttFile);
    const newAttFiles = addedAttachments.map(urlToAttFile);

    return Promise.all([
      ...landlordsIds.map(async (landlordId) => {
        this.dbSrv.estateFactory.requireContext(estateId);
        const dbAttFiles = await this.getFiles(agreementId, landlordId);

        await this.setFiles(agreementId, landlordId, {
          estateId: estateId,
          files: [
            ...dbAttFiles.filter((db) => !deletedAttUrls.find((del) => del === db.url)),
            ...oldAttFiles.filter((old) => !dbAttFiles.find((db) => db.url === old.url)),
            ...newAttFiles
          ]
        });
      }),
      ...removedLandlordsIds.map(async (landlordId) => {
        this.dbSrv.estateFactory.requireContext(estateId);
        const files = await this.getFiles(agreementId, landlordId);

        await this.setFiles(agreementId, landlordId, {
          estateId: estateId,
          files: files.filter((f) => !oldAttFiles.find((att) => f.url === att.url))
        });
      })
    ]);
  }

  public setAgreement(estateId: string, agreementId: string, landlords: string[], attachments: FileInStorageData[]) {
    const uploadDate = Date.now();
    const newFiles: AttFile[] = attachments.map((x, i) => {
      return {
        uploadDate,
        name: x.name ?? `file_${i}`,
        url: x.storagePath,
        fileCategory: FileCategory.AGREEMENT,
        visibleInApp: true,
        referenceId: agreementId
      };
    });

    return Promise.all(
      landlords.map(async (landlordId) => {
        this.dbSrv.estateFactory.requireContext(estateId);
        const oldFiles = await this.getFiles(agreementId, landlordId);
        await this.setFiles(agreementId, landlordId, {
          estateId: estateId,
          files: oldFiles.filter((x) => x.fileCategory !== FileCategory.AGREEMENT).concat(newFiles)
        });
      })
    );
  }

  public setProtocol(
    estateId: string,
    agreementId: string,
    protocolId: string,
    landlords: string[],
    attachments: FileInStorageData[]
  ) {
    const uploadDate = Date.now();
    const newFiles: AttFile[] = attachments.map((x, i) => {
      return {
        uploadDate,
        name: x.name ?? `file_${i}`,
        url: x.storagePath,
        fileCategory: FileCategory.PROTOCOL,
        visibleInApp: true,
        referenceId: protocolId
      };
    });

    return Promise.all(
      landlords.map(async (landlordId) => {
        this.dbSrv.estateFactory.requireContext(estateId);
        const oldFiles = await this.getFiles(agreementId, landlordId);
        await this.setFiles(agreementId, landlordId, {
          estateId: estateId,
          files: oldFiles.filter((x) => x.referenceId !== protocolId).concat(newFiles)
        });
      })
    );
  }
}
